import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Dialog, DialogContent, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DialogTitle } from "./dialog-title";
import { SearchInputField } from "./search-input-field";
import { ResultTabs } from "./result-tabs";
import { ResultList } from "./result-list";
import { SearchSkeleton } from "./search-skeleton";
import { NoResultText } from "./no-result-text";
import { SearchPlaceholder } from "./search-placeholder";
import { useSearchDialog, TABS } from "./use-search-dialog";

export const SearchDialog = ({ onClose, open = false, ...other }) => {
  const { t } = useTranslation();
  const {
    query, activeTab, setActiveTab, semanticLevel,
    categoryCounts, visibleResults, activeCategory,
    searchRolodex, hasMore, fetching, results, lazyLoadRef, scrollContainerRef,
    handleChange, handleClear, handleClose, handleResultClick,
    runSearch, setSemanticWeight,
    showSuggested, showSkeleton, showError, showNoResults, showResults,
  } = useSearchDialog({ onClose });

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      onClose={handleClose}
      open={open}
      {...other}
      sx={{ "& .MuiDialog-paper": { height: "90vh", minHeight: "90vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle onClose={handleClose} />
      <Divider />

      <DialogContent sx={{ pb: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <SearchInputField
          query={query}
          fetching={fetching && results.length === 0}
          semanticLevel={semanticLevel}
          onChange={handleChange}
          onClear={handleClear}
          onSemanticChange={setSemanticWeight}
          onApply={runSearch}
        />

        {showResults && (
          <ResultTabs
            tabs={TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            counts={categoryCounts}
            totalCount={searchRolodex?.totalCount ?? results.length}
          />
        )}

        <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: fetching && results.length > 0 ? "hidden" : "auto", pb: 3 }}>
          {showResults && (
            <>
              <ResultList
                visibleResults={visibleResults}
                activeCategory={activeCategory}
                onResultClick={handleResultClick}
              />
              <Box ref={lazyLoadRef} sx={{ height: 1 }} />
              {!hasMore && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2, mt: 1, borderRadius: 2, backgroundColor: "action.hover" }}>
                  <Divider sx={{ flex: 1 }} />
                  <Box component="span" sx={{ fontSize: "0.8rem", fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {t("search.endOfResults")}
                  </Box>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              )}
            </>
          )}

          {showSkeleton && <SearchSkeleton />}
          {showError && <Alert severity="error" sx={{ mt: 3 }}>{t("search.error")}</Alert>}
          {showNoResults && <NoResultText query={query} />}
          {showSuggested && (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pb: "20%" }}>
              <SearchPlaceholder />
            </Box>
          )}
        </Box>

        {fetching && results.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 2, borderTop: 2, borderColor: "primary.main", backgroundColor: "action.hover" }}>
            <CircularProgress size={18} thickness={5} />
            <Box component="span" sx={{ fontSize: "0.85rem", fontWeight: 500, color: "text.primary" }}>
              {t("search.loadingMore")}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

SearchDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
